import type { ValidationFunctionName } from '../validation'

type FieldDefinition = {
  label: string
  name: string
  inputType: 'textfield' | 'multitext' | 'dropdown' | 'multiselect' | 'textarea'
  platform?: 'cat' | 'perkins'
  validations?: Validation[]
  appendable?: boolean
}

type Validation = {
  type: ValidationFunctionName
  params?: any[]
}

const fields: Record<string, FieldDefinition> = {
  engineMarket: {
    label: 'Engine Markets',
    name: 'engineMarket',
    inputType: 'multiselect',
    appendable: true,
    validations: [{ type: 'required' }, { type: 'minimum', params: [1] }],
  },
  category: {
    label: 'Category',
    name: 'category',
    inputType: 'dropdown',
    validations: [{ type: 'required' }],
  },
  productModel: {
    label: 'Product Models',
    name: 'productModel',
    inputType: 'multiselect',
    appendable: true,
  },
  subIndustry: {
    label: 'Sub-Industry',
    name: 'subIndustry',
    inputType: 'multiselect',
    appendable: true,
    platform: 'cat',
  },
  productType: {
    label: 'Product Types',
    name: 'productType',
    inputType: 'multiselect',
    appendable: true,
  },
  productId: {
    label: 'Product Id',
    name: 'productId',
    inputType: 'multiselect',
    appendable: true,
  },
  duty: {
    label: 'Duties',
    name: 'duty',
    inputType: 'multiselect',
    appendable: true,
  },
  fuelType: {
    label: 'Fuel Types',
    name: 'fuelType',
    inputType: 'multiselect',
    appendable: true,
  },
  frequency: {
    label: 'Frequencies',
    name: 'frequency',
    inputType: 'multiselect',
    appendable: true,
  },
  voltage: {
    label: 'Voltages',
    name: 'voltage',
    inputType: 'multiselect',
    appendable: true,
  },
  ratingKva: {
    label: 'Rating kVA',
    name: 'ratingKva',
    inputType: 'multitext',
    appendable: true,
    validations: [
      { type: 'integerArray' },
      { type: 'maxCharArray', params: [29] },
    ],
  },
  ratingBkw: {
    label: 'Rating bkW',
    name: 'ratingBkw',
    inputType: 'multitext',
    appendable: true,
    validations: [
      { type: 'integerArray' },
      { type: 'maxCharArray', params: [29] },
    ],
  },
  ratingBhp: {
    label: 'Rating bhp',
    name: 'ratingBhp',
    inputType: 'multitext',
    appendable: true,
    validations: [
      { type: 'integerArray' },
      { type: 'maxCharArray', params: [29] },
    ],
  },
  ratingEkw: {
    label: 'Rating ekW',
    name: 'ratingEkw',
    inputType: 'multitext',
    appendable: true,
    validations: [
      { type: 'integerArray' },
      { type: 'maxCharArray', params: [29] },
    ],
  },
  ratingMhp: {
    label: 'Rating mhp',
    name: 'ratingMhp',
    inputType: 'multitext',
    appendable: true,
    validations: [
      { type: 'integerArray' },
      { type: 'maxCharArray', params: [29] },
    ],
  },
  language: {
    label: 'Languages',
    name: 'language',
    inputType: 'multiselect',
    appendable: true,
    validations: [{ type: 'required' }, { type: 'minimum', params: [1] }],
  },
  affiliationCodes: {
    label: 'Affiliation Codes',
    name: 'affiliationCodes',
    inputType: 'multiselect',
    appendable: true,
    validations: [{ type: 'required' }, { type: 'minimum', params: [1] }],
  },
  rpm: {
    label: 'RPMs',
    name: 'rpm',
    inputType: 'multiselect',
    appendable: true,
  },
  emissionTier: {
    label: 'Emission Tiers',
    name: 'emissionTier',
    inputType: 'multiselect',
    appendable: true,
    platform: 'cat',
  },
  targetRegion: {
    label: 'Target Regions',
    name: 'targetRegion',
    inputType: 'multiselect',
    appendable: true,
  },
  performanceNumber: {
    label: 'Performance Number',
    name: 'performanceNumber',
    inputType: 'multitext',
    appendable: true,
    validations: [{ type: 'maxCharArray', params: [50] }],
  },
  source: {
    label: 'Source',
    name: 'source',
    inputType: 'multiselect',
    appendable: true,
  },
  application: {
    label: 'Applications',
    name: 'application',
    inputType: 'multiselect',
    appendable: true,
  },
  excitation: {
    label: 'Excitation',
    name: 'excitation',
    inputType: 'multiselect',
    appendable: true,
  },
  generatorArrangement: {
    label: 'Generator Arrangement',
    name: 'generatorArrangement',
    inputType: 'multitext',
    appendable: true,
  },
  featureCode: {
    label: 'Feature Code',
    name: 'featureCode',
    inputType: 'multitext',
    appendable: true,
    platform: 'cat',
  },
  keywords: {
    label: 'Keywords',
    name: 'keywords',
    inputType: 'multitext',
    appendable: true,
    validations: [{ type: 'maxCharArray', params: [50] }],
  },
  longDescription: {
    label: 'Long Description',
    name: 'longDescription',
    inputType: 'textarea',
    validations: [{ type: 'maxChars', params: [8000] }],
  },
  restrictions: {
    label: 'Restrictions',
    name: 'restrictions',
    inputType: 'textfield',
  },
  mediaStatus: {
    label: 'Media Status',
    name: 'mediaStatus',
    inputType: 'dropdown',
    validations: [{ type: 'required' }],
  },
  supportName: {
    label: 'Support Name',
    name: 'supportName',
    inputType: 'textfield',
    validations: [{ type: 'maxChars', params: [50] }],
  },
  supportPhone: {
    label: 'Support Phone',
    name: 'supportPhone',
    inputType: 'textfield',
    validations: [{ type: 'phone' }],
  },
  supportEmail: {
    label: 'Support Email',
    name: 'supportEmail',
    inputType: 'textfield',
    validations: [{ type: 'email' }, { type: 'maxChars', params: [50] }],
  },
  countryOfUse: {
    label: 'Country of Use',
    name: 'countryOfUse',
    inputType: 'multiselect',
    appendable: true,
  },
  configurableSalesModel: {
    label: 'Configurable Sales Model',
    name: 'configurableSalesModel',
    inputType: 'multitext',
    appendable: true,
    platform: 'cat',
  },
  providerMemo: {
    label: 'Provider Memo',
    name: 'providerMemo',
    inputType: 'textfield',
  },
  supplierNotes: {
    label: 'Supplier Notes',
    name: 'supplierNotes',
    inputType: 'textfield',
    validations: [{ type: 'maxChars', params: [200] }],
  },
}

export default fields
